/**
 * Kaltura brain-stall instrumentation.
 *
 * Symptom this exists for: mid-conversation, after a handful of document tool
 * calls, the avatar simply stops. Our reply path is fine — every call is ACKed
 * and accepted — and Kaltura's own SDK is the one that raises `brainStalled`.
 * Because nothing throws, the session produced no error report at all; the only
 * trace was ordinary log lines nobody reads.
 *
 * So: keep a bounded trail of the turn-level events, and hand it to the stall
 * report so each stall arrives with the sequence that led to it.
 */

/** Events worth keeping; anything else is noise at stall time. */
export type TrailKind =
  | "tool_call"
  | "tool_reply"
  | "tool_dropped"
  | "speech_start"
  | "speech_stop"
  | "stall"
  | "nudge"
  | "recovered";

export interface TrailEntry {
  kind: TrailKind;
  /** ms since the trail was created — compact and directly comparable. */
  at: number;
  detail?: Record<string, unknown>;
}

/** Kept small on purpose: the payload rides inside one log/Sentry event. */
const TRAIL_LIMIT = 20;

export interface SessionTrail {
  record(kind: TrailKind, detail?: Record<string, unknown>): void;
  /** Oldest → newest, each as `+<seconds>s <kind> <k=v…>`. */
  snapshot(): string[];
  /** ms since the last speech start, or null when the avatar never spoke. */
  msSinceSpeech(): number | null;
  toolCalls(): number;
}

export function createSessionTrail(): SessionTrail {
  const startedAt = Date.now();
  const entries: TrailEntry[] = [];
  let lastSpeechAt: number | null = null;
  let toolCallCount = 0;

  return {
    record(kind, detail) {
      const now = Date.now();
      if (kind === "speech_start") lastSpeechAt = now;
      if (kind === "tool_call") toolCallCount += 1;
      entries.push({ kind, at: now - startedAt, detail });
      if (entries.length > TRAIL_LIMIT) entries.shift();
    },
    snapshot() {
      return entries.map((e) => {
        const detail = e.detail
          ? Object.entries(e.detail)
              .filter(([, v]) => v !== null && v !== undefined)
              .map(([k, v]) => `${k}=${String(v)}`)
              .join(" ")
          : "";
        return `+${(e.at / 1000).toFixed(1)}s ${e.kind}${detail ? ` ${detail}` : ""}`;
      });
    },
    msSinceSpeech() {
      return lastSpeechAt === null ? null : Date.now() - lastSpeechAt;
    },
    toolCalls() {
      return toolCallCount;
    },
  };
}

/**
 * `speak()` is not text-to-speech — it emits `onTextEntered`, i.e. a learner
 * turn into the brain. That makes it the only lever we have to restart a
 * stalled turn from the client. Phrased as the learner so the brain's reply
 * stays in character.
 */
export const STALL_NUDGE_TEXT = "Please continue.";

/**
 * How long we give the nudged brain before telling the learner something is
 * wrong. Recovered turns in the logs speak back within ~1.5s, so this is
 * generous without leaving anyone staring at a frozen face.
 */
export const STALL_NOTICE_DELAY_MS = 6000;
